import { useState, useEffect } from "react";
import { AppLayout } from "@/components/layout/AppLayout";
import { useProcurement } from "@/context/ProcurementContext";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { 
  Package, 
  FileText, 
  CheckCircle,
  AlertTriangle,
  Clock,
  Calculator,
  Truck,
  Target,
  X,
  MapPin
} from "lucide-react";
import { formatCurrency } from "@/lib/utils";
import { toast } from "sonner";

interface QuantityTestItem {
  itemId: number;
  skuId: number;
  itemName: string;
  skuCode: string;
  poQuantity: number;
  receivedQuantity: number;
  acceptedQuantity: number;
  rejectedQuantity: number;
  location?: string; // where the item is placed in store
  placedQuantity?: number; // quantity actually placed when location is changed/full
  notes: string;
}

const QuantityTesting = () => {
  const { state, actions } = useProcurement();
  const [showTestingModal, setShowTestingModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
  const [testItems, setTestItems] = useState<QuantityTestItem[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [overallRemarks, setOverallRemarks] = useState('');

  useEffect(() => {
    actions.fetchInvoices();
  }, []);

  // Filter invoices that have been handed over and need quantity testing
  const readyForQuantityTesting = state.invoices.filter(invoice => {
    return (invoice.status === 'Fully Accepted' || invoice.status === 'Partly Accepted') && 
           invoice.qualityTested === true &&
           invoice.qualityRating >= 3.0; // Only show invoices that passed IQC
  });

  // Debug logging for Quantity Testing
  console.log('Quantity Testing - Total invoices:', state.invoices.length);
  console.log('Quantity Testing - Ready for testing:', readyForQuantityTesting.length);
  console.log('Quantity Testing - Invoices:', readyForQuantityTesting.map(inv => ({
    id: inv.id,
    invoiceNumber: inv.invoiceNumber,
    status: inv.status,
    qualityTested: inv.qualityTested,
    qualityRating: inv.qualityRating
  })));

  const handleStartTesting = (invoice: any) => {
    setSelectedInvoice(invoice);
    
    // Initialize test items from PO items (we'll need to get this from the related PO)
    const relatedPO = state.purchaseOrders.find(po => po.poNumber === invoice.poNumber);
    if (relatedPO && relatedPO.items) {
      const initialTestItems = relatedPO.items.map((item: any, index: number) => ({
        itemId: item.id || index + 1,
      skuId: item.skuId,
        itemName: item.itemName,
      skuCode: item.skuCode,
        poQuantity: item.quantity,
        receivedQuantity: 0,
        acceptedQuantity: 0,
        rejectedQuantity: 0,
        location: '',
        placedQuantity: 0,
      notes: ''
    }));
      setTestItems(initialTestItems);
    }
    
    setShowTestingModal(true);
  };

  const updateTestItem = (index: number, field: keyof QuantityTestItem, value: any) => {
    setTestItems(prev => prev.map((item, i) => 
      i === index ? { ...item, [field]: value } : item
    ));
  };

  const calculateAcceptedRejected = (index: number, receivedQty: number) => {
    const item = testItems[index];
    if (receivedQty >= item.poQuantity) {
      // Full quantity received
      updateTestItem(index, 'acceptedQuantity', item.poQuantity);
      updateTestItem(index, 'rejectedQuantity', receivedQty - item.poQuantity);
    } else {
      // Partial quantity received
      updateTestItem(index, 'acceptedQuantity', receivedQty);
      updateTestItem(index, 'rejectedQuantity', 0);
    }
  };

  const handleSubmitTesting = async () => {
    if (testItems.some(item => item.receivedQuantity === 0)) {
      toast.error('Please enter received quantities for all items');
      return;
    }

    setIsSubmitting(true);
    try {
      // Calculate overall results
      const totalPOQuantity = testItems.reduce((sum, item) => sum + item.poQuantity, 0);
      const totalReceivedQuantity = testItems.reduce((sum, item) => sum + item.receivedQuantity, 0);
      const totalAcceptedQuantity = testItems.reduce((sum, item) => sum + item.acceptedQuantity, 0);
      const totalRejectedQuantity = testItems.reduce((sum, item) => sum + item.rejectedQuantity, 0);

      // Determine final status based on quantities
      let finalStatus = 'Pending';
      let receivedStatus = 'Not Received';

      if (totalAcceptedQuantity >= totalPOQuantity) {
        finalStatus = 'Pending'; // Ready for manager submission
        receivedStatus = 'Received';
      } else if (totalAcceptedQuantity > 0) {
        finalStatus = 'Pending'; // Ready for manager submission
        receivedStatus = 'Received';
      } else {
        finalStatus = 'Rejected'; // Not submitted to manager
        receivedStatus = 'Not Received';
      }

      // Detect location changes and send alerts if location provided/changed
      const itemsWithLocation = testItems.filter(t => (t.location && t.location.trim().length > 0) || (t.placedQuantity && t.placedQuantity > 0));
      if (itemsWithLocation.length > 0) {
        itemsWithLocation.forEach((t) => {
          // fire-and-forget alert into alert center via handover logs/alerts context if available
          console.log('ALERT: Location change/placement', {
            invoiceId: selectedInvoice.id,
            invoiceNumber: selectedInvoice.invoiceNumber,
            poNumber: selectedInvoice.poNumber,
            item: t.itemName,
            location: t.location,
            placedQuantity: t.placedQuantity,
            by: state.currentUser?.name || 'Floor Manager'
          });
        });
      }

      // Update invoice with quantity testing results
      await actions.updateInvoice(selectedInvoice.id, {
        status: finalStatus,
        receivedStatus: receivedStatus,
        quantityTested: true,
        quantityTestedBy: state.currentUser?.id || 1,
        quantityTestedAt: new Date().toISOString(),
        quantityTestResults: {
          items: testItems,
          totalPOQuantity,
          totalReceivedQuantity,
          totalAcceptedQuantity,
          totalRejectedQuantity,
          remarks: overallRemarks
        },
        updatedAt: new Date().toISOString()
      });

      toast.success('Quantity testing completed successfully');
      setShowTestingModal(false);
      setSelectedInvoice(null);
      setTestItems([]);
    } catch (error) {
      console.error('Failed to complete quantity testing:', error);
      toast.error('Failed to complete quantity testing');
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = Date.now();
    const diff = now - date.getTime();
    const hours = Math.floor(diff / (1000 * 60 * 60));
    return `${hours}h ago`;
  };

  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Quantity Testing</h1>
            <p className="text-muted-foreground">
              Test received quantities before submitting to Manager
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="flex items-center gap-1">
              <Package className="h-3 w-3" />
              {readyForQuantityTesting.length} Ready for Testing
            </Badge>
          </div>
        </div>

        {/* Invoices Ready for Quantity Testing */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
              <Target className="h-5 w-5" />
              Invoices Ready for Quantity Testing
              </CardTitle>
              <CardDescription>
              Invoices that have passed IQC testing and been handed over
              </CardDescription>
            </CardHeader>
            <CardContent>
            <div className="space-y-4">
              {readyForQuantityTesting.map((invoice) => (
                <div key={invoice.id} className="flex items-center justify-between p-4 border rounded-lg">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-blue-100 dark:bg-blue-900 rounded-lg">
                      <Package className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold">{invoice.invoiceNumber}</h4>
                        <Badge variant="outline">{invoice.poNumber}</Badge>
                        </div>
                      <p className="text-sm text-muted-foreground">{invoice.vendorName}</p>
                      <div className="flex items-center gap-4 mt-1">
                        <span className="text-sm text-muted-foreground">
                          IQC Rating: {invoice.qualityRating}/5
                        </span>
                        <span className="text-sm text-muted-foreground">
                          Handed Over: {formatTime(invoice.updatedAt || invoice.createdAt)}
                        </span>
                            </div>
                            </div>
                        </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                      <Clock className="h-3 w-3 mr-1" />
                      Awaiting Quantity Test
                    </Badge>
                        <Button
                      onClick={() => handleStartTesting(invoice)}
                          size="sm"
                          className="flex items-center gap-2"
                        >
                      <Calculator className="h-4 w-4" />
                      Start Testing
                        </Button>
                  </div>
                </div>
              ))}
              
              {readyForQuantityTesting.length === 0 && (
                <div className="text-center py-8">
                  <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No invoices ready for quantity testing</h3>
                  <p className="text-muted-foreground">
                    Invoices will appear here after IQC testing and handover.
                  </p>
                          </div>
                        )}
                          </div>
          </CardContent>
        </Card>

        {/* Quantity Testing Modal */}
        <Dialog open={showTestingModal} onOpenChange={setShowTestingModal}>
          <DialogContent className="max-w-4xl h-[90vh] p-0 overflow-hidden">
            <div className="flex flex-col h-full min-h-0">
              <div className="shrink-0 sticky top-0 z-10 bg-background border-b p-4">
                <div className="flex items-start justify-between">
            <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                      <Calculator className="h-5 w-5" />
                      Quantity Testing - {selectedInvoice?.invoiceNumber}
                    </DialogTitle>
              <DialogDescription>
                      Test received quantities against PO quantities
              </DialogDescription>
            </DialogHeader>
                  <button
                    aria-label="Close"
                    onClick={() => setShowTestingModal(false)}
                    className="ml-4 inline-flex h-8 w-8 items-center justify-center rounded-md border hover:bg-muted"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            
            {selectedInvoice && (
              <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-4">
                {/* Invoice Information */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">Invoice Details</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-sm font-medium">Invoice Number</Label>
                        <p className="text-sm text-muted-foreground">{selectedInvoice.invoiceNumber}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">PO Number</Label>
                        <p className="text-sm text-muted-foreground">{selectedInvoice.poNumber}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Vendor</Label>
                        <p className="text-sm text-muted-foreground">{selectedInvoice.vendorName}</p>
                      </div>
                      <div>
                        <Label className="text-sm font-medium">Total Amount</Label>
                        <p className="text-sm text-muted-foreground">{formatCurrency(selectedInvoice.totalAmount)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Quantity Testing Items */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold">Quantity Testing Items</h3>
                  
                  {testItems.map((item, index) => (
                    <Card key={index}>
                      <CardHeader className="pb-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <CardTitle className="text-base">{item.itemName}</CardTitle>
                            <p className="text-sm text-muted-foreground">{item.skuCode}</p>
                          </div>
                          <Badge variant="outline">Item {index + 1}</Badge>
                            </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                            <div>
                            <Label className="text-sm font-medium">PO Quantity</Label>
                            <Input value={item.poQuantity} readOnly className="bg-muted" />
                            </div>
                            <div>
                            <Label className="text-sm font-medium">Received Quantity</Label>
                              <Input
                                type="number"
                                value={item.receivedQuantity}
                              onChange={(e) => {
                                const value = parseInt(e.target.value) || 0;
                                updateTestItem(index, 'receivedQuantity', value);
                              }}
                              min="0"
                              />
                            </div>
                          <div>
                            <Label className="text-sm font-medium flex items-center gap-2"><MapPin className="h-3 w-3"/>Location</Label>
                            <Input
                              placeholder="Rack/Zone/Area"
                              value={item.location || ''}
                              onChange={(e) => updateTestItem(index, 'location', e.target.value)}
                              />
                            </div>
                            <div>
                            <Label className="text-sm font-medium">Quantities Placed</Label>
                              <Input
                                type="number"
                              value={item.placedQuantity || 0}
                              onChange={(e) => updateTestItem(index, 'placedQuantity', parseInt(e.target.value) || 0)}
                              min="0"
                            />
                          </div>
                          {/* Accepted/Rejected removed */}
                            </div>
                            
                            <div>
                          <Label htmlFor={`notes-${index}`}>Testing Notes</Label>
                          <Textarea
                            id={`notes-${index}`}
                            placeholder="Enter observations, defects, or quantity notes..."
                            value={item.notes}
                            onChange={(e) => updateTestItem(index, 'notes', e.target.value)}
                            rows={2}
                              />
                          </div>
                          
                        {/* Validation helper removed */}
                        </CardContent>
                      </Card>
                    ))}
                </div>

                {/* Overall Remarks and Location Capacity Note */}
                  <Card>
                    <CardHeader>
                    <CardTitle className="text-base">Remarks / Capacity Notes</CardTitle>
                    <CardDescription>
                      Add remarks for missing quantities or storage capacity issues.
                    </CardDescription>
                    </CardHeader>
                  <CardContent className="space-y-2">
                    <Textarea
                      placeholder="Remarks about missing quantities, capacity full, alternative location, etc."
                      value={overallRemarks}
                      onChange={(e) => setOverallRemarks(e.target.value)}
                      rows={3}
                    />
                    </CardContent>
                  </Card>

                <div className="shrink-0 sticky bottom-0 z-10 border-t bg-background p-4 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowTestingModal(false)} disabled={isSubmitting}>Cancel</Button>
                  <Button onClick={handleSubmitTesting} disabled={isSubmitting || testItems.some(item => item.receivedQuantity === 0)} className="flex items-center gap-2">
                    {isSubmitting ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Submitting...
                      </>
                    ) : (
                      <>
                        <Truck className="h-4 w-4" />
                        Submit to Manager
                      </>
                    )}
                  </Button>
                </div>
              </div>
            )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
};

export default QuantityTesting;

